import { useEffect, useRef, useState } from "react";
import { refreshToken } from "../services/auth";

// Quanto tempo sem interação antes de considerar o usuário inativo
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;      // 30 min

// Começa tentativa de refresh quando restar menos que este tempo
const REFRESH_THRESHOLD_MS = 10 * 60 * 1000; // 10 min

const isDev = process.env.NODE_ENV === "development";

// Eventos que indicam atividade do usuário
const ACTIVITY_EVENTS = [
  "mousemove",
  "keydown",
  "click",
  "scroll",
  "touchstart",
  "pointerdown",
];

export default function useSessionTimer(onExpire) {
  // Inicializa com o tempo real restante (não null → evita mostrar 0:00 logo de cara)
  const initialExp  = Number(localStorage.getItem("expiresAt")) || 0;
  const initialDiff = initialExp ? initialExp - Date.now() : null;
  const [remainingTime, setRemaining] = useState(
    initialDiff && initialDiff > 0 ? initialDiff : null
  );

  const onExpireRef      = useRef(onExpire);
  const lastActiveRef    = useRef(Date.now());
  const refreshingRef    = useRef(false);
  const expiredRef       = useRef(false);   // evita disparar onExpire mais de uma vez
  // refreshFailedRef só vira true quando o BACKEND rejeita o refresh token
  // (expirado/revogado de verdade) — nunca por falha de rede, ver tick() abaixo.
  const refreshFailedRef = useRef(false);
  const expiresAtRef     = useRef(initialExp);
  // Limita as tentativas de renovar no bloco "já expirou" a 1 a cada 15s —
  // sem isso, uma queda de rede persistente bateria no endpoint de refresh
  // a cada tick (1s) até a conexão voltar.
  const lastRetryRef     = useRef(0);

  // Mantém referência atualizada sem disparar efeito
  useEffect(() => { onExpireRef.current = onExpire; }, [onExpire]);

  // Rastreia atividade do usuário
  useEffect(() => {
    const mark = () => { lastActiveRef.current = Date.now(); };
    ACTIVITY_EVENTS.forEach((ev) =>
      window.addEventListener(ev, mark, { passive: true })
    );
    return () =>
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, mark));
  }, []);

  useEffect(() => {
    const tick = () => {
      if (expiredRef.current) return;

      // Sempre re-lê o localStorage: o interceptor do api.js pode ter renovado o
      // token de forma reativa (via 401) sem que este hook saiba.
      const storedExp = Number(localStorage.getItem("expiresAt")) || 0;
      if (storedExp && storedExp !== expiresAtRef.current) {
        expiresAtRef.current = storedExp;
      }

      const expiresAt = expiresAtRef.current;
      if (!expiresAt) return;

      const now    = Date.now();
      const diff   = expiresAt - now;
      const idleMs = now - lastActiveRef.current;
      const active = idleMs < IDLE_TIMEOUT_MS;

      // Usuário ativo + token prestes a expirar → renova silenciosamente
      if (diff > 0 && diff < REFRESH_THRESHOLD_MS && active && !refreshingRef.current && !refreshFailedRef.current) {
        if (isDev) console.log("[timer] refresh proativo — diff:", Math.round(diff/1000), "s");
        refreshingRef.current = true;
        refreshToken()
          .then((token) => {
            if (token) {
              const newExp = Number(localStorage.getItem("expiresAt")) || 0;
              if (newExp) {
                expiresAtRef.current = newExp;
                refreshFailedRef.current = false;
              }
            } else {
              // Backend rejeitou o refresh token — expirado/revogado de verdade.
              refreshFailedRef.current = true;
            }
          })
          .catch(() => {
            // Falha de rede/servidor (ver services/auth.js) — não marca como
            // falha definitiva, só tenta de novo num próximo tick.
            if (isDev) console.log("[timer] refresh proativo falhou por rede — mantendo sessão");
          })
          .finally(() => { refreshingRef.current = false; });
      }

      // Achado da revisão de sessão (06/09/2026): antes, chegar aqui SEMPRE
      // deslogava — inclusive quando o usuário só estava inativo (o bloco
      // acima não roda se `active` for false) e o refresh token continuava
      // válido por até 15 dias. Ficar de telefone no bolso entre séries de
      // um treino, por exemplo, bastava pra ser deslogado sem necessidade.
      // Agora, antes de desistir, tenta renovar UMA vez mesmo com o usuário
      // inativo — só desloga de fato se o backend confirmar que o refresh
      // token é inválido (refreshFailedRef), nunca por estar parado ou por
      // falha de rede.
      if (diff <= 0 && !refreshingRef.current) {
        const freshExp = Number(localStorage.getItem("expiresAt")) || 0;
        if (freshExp > now) {
          expiresAtRef.current = freshExp;
          setRemaining(freshExp - now);
          return;
        }

        if (refreshFailedRef.current) {
          // Já confirmamos com o backend que o refresh token é inválido —
          // não insiste, desloga de verdade.
          expiredRef.current = true;
          onExpireRef.current?.();
          return;
        }

        if (now - lastRetryRef.current < 15000) return;
        lastRetryRef.current = now;

        refreshingRef.current = true;
        refreshToken()
          .then((token) => {
            if (token) {
              const newExp = Number(localStorage.getItem("expiresAt")) || 0;
              if (newExp) expiresAtRef.current = newExp;
            } else {
              // Backend confirmou: refresh token inválido/expirado — logout real.
              refreshFailedRef.current = true;
              expiredRef.current = true;
              onExpireRef.current?.();
            }
          })
          .catch(() => {
            // Falha de rede/servidor — não desloga, tenta de novo em até 15s.
            if (isDev) console.log("[timer] refresh de recuperação falhou por rede — mantendo sessão");
          })
          .finally(() => { refreshingRef.current = false; });
        return;
      }

      if (diff > 0) setRemaining(diff);
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  return remainingTime;
}
